import { useState, useEffect } from 'react';
import { getUserInfo, AvailableModel, KnowledgeBase, Subscription, TokenUsage } from '../services/ai/fastApiBackend';
import { getUserIdentity } from '../services/identity';
import { createOfficeContext } from '../services/office/factory';
import { IOfficeContext, InitResult, HostType } from '../types/office';
import { logger } from '../utils/logger';

export function useOfficeInitialization(standalone: boolean = false): InitResult {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isLicenseError, setIsLicenseError] = useState<boolean>(false);
  const [requiresPopup, setRequiresPopup] = useState<boolean>(false);
  const [loginHint, setLoginHint] = useState<string | null>(null);
  const [context, setContext] = useState<IOfficeContext | null>(null);
  const [item, setItem] = useState<Office.MessageRead | Office.MessageCompose | null>(null);
  const [mode, setMode] = useState<"read" | "compose" | null>(null);
  const [userName, setUserName] = useState<string>("");
  const [userEmail, setUserEmail] = useState<string>("");
  const [senderName, setSenderName] = useState<string>("");
  const [availableModels, setAvailableModels] = useState<AvailableModel[]>([]);
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [tokenUsage, setTokenUsage] = useState<TokenUsage | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      logger.debug("[Office Init] Starting initialization...");
      
      try {
        // 1. Wait for Office
        logger.debug("[Office Init] Step 1: Waiting for Office.onReady()...");
        await Office.onReady();
        logger.debug("[Office Init] Step 1: Office.onReady() completed");
        
        const host = Office.context.host as HostType; // e.g., "Outlook", "Word"
        logger.debug(`[Office Init] Step 1: Office Ready - Host detected: ${host}`);
        logger.debug(`[Office Init] Step 1: Office.context available:`, !!Office.context);
        logger.debug(`[Office Init] Step 1: Office.context.host value:`, Office.context.host);
        logger.debug(`[Office Init] Step 1: Office.context.host type:`, typeof Office.context.host);

        // 2. Get Identity (Common across all apps)
        logger.debug("[Office Init] Step 2: Getting user identity...");
        let identityResult;
        try {
          // Try silent authentication first (don't auto-trigger popup)
          identityResult = await getUserIdentity(false);
          logger.debug("[Office Init] Step 2: Identity retrieved:", {
            hasIdentity: !!identityResult.identity,
            hasEmail: !!identityResult.identity?.email,
            hasDisplayName: !!identityResult.identity?.displayName,
            requiresPopup: identityResult.requiresPopup,
          });

          // If popup is required, set state and return early
          if (identityResult.requiresPopup) {
            logger.debug("[Office Init] Step 2: Popup authentication required");
            if (isMounted) {
              setRequiresPopup(true);
              setLoginHint(identityResult.loginHint || null);
            }
            return; // Stop initialization, wait for user to click login button
          }
        } catch (identityError) {
          logger.error("[Office Init] Step 2: Error getting identity:", identityError);
          throw identityError;
        }
        
        const identity = identityResult.identity;
        const email = identity?.email || "";
        let name = identity?.displayName || "";
        const hasToken = !!identity?.rawAccessToken;

        // For personal accounts, tokens are opaque and cannot be JWT decoded
        // If we have a token but no email, it's likely a personal account - continue anyway
        // The backend will handle authentication with the opaque token
        if (!email && !hasToken) {
          logger.warn("[Office Init] Step 2: Could not determine user identity - no email and no token found");
          logger.warn("[Office Init] Step 2: Identity summary:", {
            hasEmail: !!identity?.email,
            hasDisplayName: !!identity?.displayName,
            hasToken: !!identity?.rawAccessToken,
          });
          if (isMounted) setIsLicenseError(true);
          return;
        }

        // If we have a token but no email (opaque token for personal account), log and continue
        if (hasToken && !email) {
          logger.debug("[Office Init] Step 2: Opaque token detected (personal account) - no email from JWT decode, but token available");
          logger.debug("[Office Init] Step 2: Backend will handle authentication with opaque token");
        }

        logger.debug("[Office Init] Step 2: User identity resolved:", {
          emailLength: email.length,
          nameLength: name.length,
          hasToken: !!identity?.rawAccessToken,
        });

        // 3. Auth with Backend (Common across all apps)
        logger.debug("[Office Init] Step 3: Authenticating with backend...");
        try {
          // Get SSO token if not already available from identity
          let token = identity?.rawAccessToken;
          if (!token) {
            // Fallback: Try OfficeRuntime.auth.getAccessToken if MSAL NAA didn't provide a token
            // This should rarely happen as all hosts now use MSAL NAA when supported
            if (typeof OfficeRuntime !== 'undefined' && OfficeRuntime?.auth?.getAccessToken) {
              logger.debug("[Office Init] Step 3: Attempting fallback token retrieval via OfficeRuntime");
              token = await OfficeRuntime.auth.getAccessToken({ allowSignInPrompt: true });
            } else {
              throw new Error('SSO token not available and cannot be retrieved. Ensure MSAL NAA is properly configured.');
            }
          }

          const userInfo = await getUserInfo(token);
          logger.debug("[Office Init] Step 3: Auth successful, user info retrieved:", {
            hasUserInfo: !!userInfo,
            userNameLength: userInfo?.user?.name?.length ?? 0,
            licenseActive: userInfo?.license?.active,
            availableModelsCount: userInfo?.available_models?.length || 0,
            knowledgeBasesCount: userInfo?.knowledge_bases?.length || 0,
          });
          
          // For personal accounts with opaque tokens, use backend user.name as fallback
          // The identity.displayName will be empty because JWT can't be decoded
          if (!name && userInfo?.user?.name) {
            name = userInfo.user.name;
            logger.debug("[Office Init] Step 3: Using user name from backend response (length):", name.length);
          }
          
          // Check if license is active - if not, set license error but continue initialization
          if (!userInfo.license?.active) {
            logger.warn("[Office Init] Step 3: License is not active - allowing UI to load with disabled inputs");
            if (isMounted) {
              setIsLicenseError(true);
            }
          }
          
          // Store available models and knowledge bases
          if (isMounted) {
            setAvailableModels(userInfo.available_models || []);
            setKnowledgeBases(userInfo.knowledge_bases || []);
            setTokenUsage(userInfo.token_usage ?? null);
            setSubscription(userInfo.subscription ?? null);
          }
        } catch (err) {
          logger.error("[Office Init] Step 3: Auth Failed", err);
          logger.error("[Office Init] Step 3: Auth error details:", {
            errorMessage: err instanceof Error ? err.message : String(err),
            errorStack: err instanceof Error ? err.stack : undefined,
          });
          if (isMounted) setIsLicenseError(true);
          return; // Stop if auth fails
        }

        // 4. Extract host-specific properties
        logger.debug("[Office Init] Step 4: Extracting host-specific properties...");
        let currentItem: Office.MessageRead | Office.MessageCompose | null = null;
        let currentMode: "read" | "compose" | null = null;
        let currentSenderName = "";

        // Outlook-specific extraction: item, mode, and sender name
        if (host === "Outlook") {
          logger.debug("[Office Init] Step 4: Outlook detected, extracting Outlook-specific properties...");
          logger.debug("[Office Init] Step 4: Office.context.mailbox available:", !!Office.context.mailbox);
          
          if (Office.context.mailbox) {
            // Try to get the item - it may be null if multi-select or other scenarios
            currentItem = Office.context.mailbox.item;
            logger.debug("[Office Init] Step 4: Outlook item retrieved:", {
              hasItem: !!currentItem,
              itemType: currentItem?.itemType
            });
            
            // Determine mode
            if (currentItem) {
              // Compose items don't expose an itemId until saved, so presence of itemId implies read mode
              const hasItemId = "itemId" in currentItem && !!(currentItem as Office.MessageRead).itemId;
              currentMode = hasItemId ? "read" : "compose";
              logger.debug("[Office Init] Step 4: Outlook mode determined:", currentMode, { hasItemId });
              
              // Get sender name (only in read mode)
              if (currentMode === "read" && "from" in currentItem && currentItem.from) {
                logger.debug("[Office Init] Step 4: Extracting sender name from read item...");
                if (typeof currentItem.from === "string") {
                  currentSenderName = currentItem.from;
                } else if ("displayName" in currentItem.from && currentItem.from.displayName) {
                  currentSenderName = currentItem.from.displayName;
                } else if ("emailAddress" in currentItem.from && currentItem.from.emailAddress) {
                  currentSenderName = currentItem.from.emailAddress;
                }
                logger.debug("[Office Init] Step 4: Sender name extracted (length):", currentSenderName.length);
              }
            } else {
              logger.warn("[Office Init] Step 4: No Outlook item found (may be multi-select or other scenario) - continuing with null item");
              // Continue anyway - chat will work with empty context
            }
          } else {
            logger.warn("[Office Init] Step 4: Office.context.mailbox not available");
          }
        } 
        // Word-specific extraction: document context (handled by DocumentAdapter, no special extraction needed)
        else if (host === "Word") {
          logger.debug("[Office Init] Step 4: Word detected, using document context");
          logger.debug("[Office Init] Step 4: Office.context.document available:", !!Office.context.document);
          // Word uses document context - no item, mode, or senderName needed
          // All Word-specific logic is handled by DocumentAdapter
        }
        // PowerPoint-specific extraction: document context (handled by DocumentAdapter, no special extraction needed)
        else if (host === "PowerPoint") {
          logger.debug("[Office Init] Step 4: PowerPoint detected, using document context");
          logger.debug("[Office Init] Step 4: Office.context.document available:", !!Office.context.document);
          // PowerPoint uses document context - no item, mode, or senderName needed
          // All PowerPoint-specific logic is handled by DocumentAdapter
        }
        // Other hosts (Excel, etc.)
        else {
          logger.debug(`[Office Init] Step 4: Other host detected (${host}), using document context`);
          logger.debug("[Office Init] Step 4: Office.context.document available:", !!Office.context.document);
        }

        logger.debug("[Office Init] Step 4: Host-specific properties:", {
          host,
          item: currentItem ? "present" : "null",
          mode: currentMode,
          senderName: currentSenderName || "N/A"
        });

        // 5. Create Context Strategy (The specific logic)
        logger.debug("[Office Init] Step 5: Creating office context...");
        logger.debug("[Office Init] Step 5: Calling createOfficeContext with:", {
          host,
          hasEmail: !!email,
        });
        let officeContext;
        try {
          officeContext = createOfficeContext(host, email);
          logger.debug("[Office Init] Step 5: Office context created:", {
            hasContext: !!officeContext,
            contextHost: officeContext?.host,
            contextMetadata: officeContext?.metadata
          });
        } catch (contextError) {
          logger.error("[Office Init] Step 5: Error creating office context:", contextError);
          logger.error("[Office Init] Step 5: Context error details:", {
            errorMessage: contextError instanceof Error ? contextError.message : String(contextError),
            errorStack: contextError instanceof Error ? contextError.stack : undefined,
            host,
            hasEmail: !!email,
          });
          throw contextError;
        }
        
        if (isMounted) {
          logger.debug("[Office Init] Step 6: Updating state with initialization results...");
          setContext(officeContext);
          setItem(currentItem);
          setMode(currentMode);
          setUserName(name);
          setUserEmail(email);
          setSenderName(currentSenderName);
          setIsReady(true);
          logger.debug("[Office Init] Step 6: State updated, initialization complete");
          logger.debug("[Office Init] Final state:", {
            isReady: true,
            isLicenseError: false,
            hasContext: !!officeContext,
            item: currentItem ? "present" : "null",
            mode: currentMode,
            hasUserName: !!name,
            hasUserEmail: !!email,
            hasSenderName: !!currentSenderName,
          });
        } else {
          logger.warn("[Office Init] Step 6: Component unmounted, skipping state update");
        }

      } catch (e) {
        logger.error("[Office Init] Initialization failed with error:", e);
        logger.error("[Office Init] Error details:", {
          errorMessage: e instanceof Error ? e.message : String(e),
          errorStack: e instanceof Error ? e.stack : undefined,
          errorName: e instanceof Error ? e.name : typeof e,
        });
        if (isMounted) {
          logger.debug("[Office Init] Setting isReady to true despite error");
          setIsReady(true); // Ready but failed
        }
      }
    };

    const initializeStandalone = async () => {
      logger.debug("[Web Init] Starting standalone (no Office host) initialization...");
      try {
        const identityResult = await getUserIdentity(false);
        if (identityResult.requiresPopup) {
          logger.debug("[Web Init] Popup sign-in required");
          if (isMounted) setRequiresPopup(true);
          return;
        }

        const identity = identityResult.identity;
        const token = identity?.rawAccessToken;
        if (!token) {
          logger.warn("[Web Init] No access token after silent auth");
          if (isMounted) { setIsLicenseError(true); setIsReady(true); }
          return;
        }

        const email = identity?.email || "";
        let name = identity?.displayName || "";

        const userInfo = await getUserInfo(token);
        if (!name && userInfo?.user?.name) {
          name = userInfo.user.name;
        }
        if (!userInfo.license?.active) {
          logger.warn("[Web Init] License not active - loading with disabled inputs");
          if (isMounted) setIsLicenseError(true);
        }

        if (isMounted) {
          setAvailableModels(userInfo.available_models || []);
          setKnowledgeBases(userInfo.knowledge_bases || []);
          setTokenUsage(userInfo.token_usage ?? null);
          setSubscription(userInfo.subscription ?? null);
          setContext(null);  // no Office host => no document/mailbox context
          setItem(null);
          setMode(null);
          setUserName(name || email);
          setUserEmail(email);
          setIsReady(true);
          logger.debug("[Web Init] Standalone initialization complete");
        }
      } catch (e) {
        logger.error("[Web Init] Standalone initialization failed:", e);
        if (isMounted) { setIsLicenseError(true); setIsReady(true); }
      }
    };

    logger.debug("[Office Init] Starting initialization process...");
    if (standalone) {
      initializeStandalone();
    } else {
      initialize();
    }

    return () => {
      logger.debug("[Office Init] Cleanup: Component unmounting");
      isMounted = false;
    };
  }, [standalone]);

  // Callback for when popup authentication succeeds
  const handlePopupSuccess = async (token: string) => {
    logger.debug("[Office Init] Popup authentication succeeded, retrying initialization...");
    setRequiresPopup(false);

    // Standalone: no Office host to read; just fetch user info and render the chat.
    if (standalone) {
      try {
        const userInfo = await getUserInfo(token);
        if (!userInfo.license?.active) setIsLicenseError(true);

        const identityResult = await getUserIdentity(true);
        const identity = identityResult.identity;
        const email = identity?.email || "";
        let name = identity?.displayName || "";
        if (!name && userInfo?.user?.name) name = userInfo.user.name;

        setAvailableModels(userInfo.available_models || []);
        setKnowledgeBases(userInfo.knowledge_bases || []);
        setTokenUsage(userInfo.token_usage ?? null);
        setSubscription(userInfo.subscription ?? null);
        setContext(null);
        setItem(null);
        setMode(null);
        setUserName(name || email);
        setUserEmail(email);
        setIsReady(true);
        logger.debug("[Web Init] Standalone initialization complete after popup auth");
      } catch (error) {
        logger.error("[Web Init] Error completing standalone init after popup auth:", error);
        setIsLicenseError(true);
      }
      return;
    }

    try {
      // Get user info with the new token
      const userInfo = await getUserInfo(token);
      
      // Check if license is active - if not, set license error but continue initialization
      if (!userInfo.license?.active) {
        logger.warn("[Office Init] License is not active after popup auth - allowing UI to load with disabled inputs");
        setIsLicenseError(true);
      }
      
      // Get identity again to get full user info
      const identityResult = await getUserIdentity(true);
      const identity = identityResult.identity;
      const email = identity?.email || "";
      let name = identity?.displayName || "";
      const hasToken = !!identity?.rawAccessToken;
      
      // For personal accounts with opaque tokens, use backend user.name as fallback
      // The identity.displayName will be empty because JWT can't be decoded
      if (!name && userInfo?.user?.name) {
        name = userInfo.user.name;
        logger.debug("[Office Init] Using user name from backend response after popup auth (length):", name.length);
      }
      
      // For personal accounts, tokens are opaque and cannot be JWT decoded
      // If we have a token but no email, it's likely a personal account - continue anyway
      // The backend will handle authentication with the opaque token
      if (!email && !hasToken) {
        logger.error("[Office Init] Could not get user identity after popup auth - no email and no token");
        setIsLicenseError(true);
        return;
      }

      // If we have a token but no email (opaque token for personal account), log and continue
      if (hasToken && !email) {
        logger.debug("[Office Init] Opaque token detected after popup auth (personal account) - no email from JWT decode, but token available");
        logger.debug("[Office Init] Backend will handle authentication with opaque token");
      }
      
      // Get host and create context
      const host = Office.context.host as HostType;
      const officeContext = createOfficeContext(host, email);
      
      // Extract Outlook-specific properties if needed
      let currentItem: Office.MessageRead | Office.MessageCompose | null = null;
      let currentMode: "read" | "compose" | null = null;
      let currentSenderName = "";
      
      if (host === "Outlook" && Office.context.mailbox) {
        const mailboxItem = Office.context.mailbox.item;
        if (mailboxItem) {
          currentItem = mailboxItem as Office.MessageRead | Office.MessageCompose;
          currentMode = mailboxItem.itemType === Office.MailboxEnums.ItemType.Message ? "read" : "compose";
          
          if (currentMode === "read" && (mailboxItem as Office.MessageRead).from) {
            currentSenderName = (mailboxItem as Office.MessageRead).from?.displayName || "";
          }
        }
      }
      
      // Update state
      setAvailableModels(userInfo.available_models || []);
      setKnowledgeBases(userInfo.knowledge_bases || []);
      setTokenUsage(userInfo.token_usage ?? null);
      setSubscription(userInfo.subscription ?? null);
      setContext(officeContext);
      setItem(currentItem);
      setMode(currentMode);
      setUserName(name);
      setUserEmail(email);
      setSenderName(currentSenderName);
      setIsReady(true);

      logger.debug("[Office Init] Initialization complete after popup auth");
    } catch (error) {
      logger.error("[Office Init] Error completing initialization after popup auth:", error);
      setIsLicenseError(true);
    }
  };

  return { 
    isReady, 
    isLicenseError,
    requiresPopup,
    loginHint,
    context,
    item,
    mode,
    userName,
    userEmail,
    senderName,
    availableModels,
    knowledgeBases,
    tokenUsage,
    subscription,
    onPopupSuccess: handlePopupSuccess
  };
}